import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AccordionComponent } from '@ppl/ui';
import { ListService } from '../../../../shared/services/list.service';
import { ListTableComponent } from '@ppl/ui';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { TableRowActionsModel } from '../../../../shared/interfaces/list.interface';
import { TableRowActions } from '../../../../shared/interfaces/actions';
import { ButtonComponent } from '@ppl/ui';
import { DialogConfig, DialogService } from '../../../../shared/services/dialog.service';
import { CreatePurchaseRequestComponent } from "../create-purchase-request/create-purchase-request.component";
import { UserService } from '../../../../services/user.service';
import { PurchaseRequestsResponse } from '../../../../interfaces/purchase-requests.interface';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { Subscription } from 'rxjs';
import { ReferralDataService } from '../../../../services/referral-data.service';
import { AuthorizationUtility } from '../../../../shared/authorization/auth.utility';
import { AllowListAuthStore } from '../../../../shared/authorization/allow-list-auth.store';
import { MEMBER_VIEW_PAGE_IDS } from '../../../../shared/constants/page.constants';

@Component({
  selector: 'ppl-purchase-request-participant-view',
  standalone: true,
  imports: [
    AccordionComponent,
    ListTableComponent,
    NgZorroModule,
    ButtonComponent,
    NzSelectModule,
    CreatePurchaseRequestComponent,
    FormsModule,
    NzFormModule,
    ReactiveFormsModule,
  ],
  templateUrl: './purchase-request-participant-view.component.html',
  styleUrl: './purchase-request-participant-view.component.css'
})
export class PurchaseRequestParticipantViewComponent implements OnInit, OnDestroy {
  @ViewChild('purchaseRequestsChildComponent') purchaseRequestsChildComponent?: ListTableComponent;
  @ViewChild('addPurchaseRequestDialog') addPurchaseRequestDialog!: TemplateRef<any>;
  @ViewChild('viewPurchaseRequestDialog') viewPurchaseRequestDialog!: TemplateRef<any>;

  actionButtonsInProcess: TableRowActionsModel = new TableRowActions({
    view: true,
  });
  addDialogId: any;
  consumerData: any = {};
  columnsToBeHidden: string[] = [];
  inprocessInvoices: any[] = [];
  isInStaffRole: boolean | undefined = false;
  mode!: "view" | "add" | "edit" | "actions";
  participantId: any;
  pageTitle: string = 'Purchase Request Summary';
  purchaseRequestSearchForm!: FormGroup;
  tableTitle: string = 'Purchase Requests'
  programName!: string;
  purchaseRequestData!: PurchaseRequestsResponse[];
  // Keep the applied filters so paging past page 1 refetches the same filtered set.
  private purchaseRequestSearchParams: any[] = [];
  viewPurchaseRequestData: any;

  cols = [
    {
      field: 'vendorName',
      header: 'Vendor',
      jsonpath: 'vendorName',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'service',
      header: 'Service',
      jsonpath: 'service',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: "status",
      header: "Status",
      jsonpath: "status",
      isDisplayedAtHeader: "y",
      isSearchable: "y"
    },
    {
      field: "totalAmount",
      header: "Total Amount",
      jsonpath: "totalAmount",
      isDisplayedAtHeader: "y",
      isSearchable: "y"
    },
    {
      field: "orderNumber",
      header: "Order Number",
      jsonpath: "orderNumber",
      isDisplayedAtHeader: "y",
      isSearchable: "y"
    },
    {
      field: "trackingNumber",
      header: "Tracking Number",
      jsonpath: "trackingNumber",
      isDisplayedAtHeader: "y",
      isSearchable: "y"
    },
    {
      field: "dateLastUpdated",
      header: "Updated Date",
      jsonpath: "dateLastUpdated",
      isDisplayedAtHeader: "y",
      isSearchable: "y"
    }
  ];

  isSectionEditable = false;
  isSectionVisible = true;
  isSectionDeletable = false;
  canApproveReject = true;

  purchaseRequestFilterStatus = [
    { lkpValueCode: 'COMPLETED', lkpValueName: 'Completed' },
    { lkpValueCode: 'REJECTED', lkpValueName: 'Rejected' },
    { lkpValueCode: 'SUBMITTED', lkpValueName: 'Submitted' }
  ];
  private getPurchaseRequestSub!: Subscription;

  constructor(
    private listService: ListService,
    private dialogSvc: DialogService,
    private referralDataService: ReferralDataService,
    private userService: UserService,
    private fb: FormBuilder,
    private authUtility: AuthorizationUtility,
    private allowListStore: AllowListAuthStore
  ) { }

  ngOnInit(): void {
    const selectedProgram = this.userService.getSelectedProgram();
    this.isInStaffRole = selectedProgram?.roles.some((role: any) => role.role_name === 'PPL Admin');
    this.programName = selectedProgram?.program_code ?? '';
    this.participantId = this.userService.getSelectedLead();
    const pageId = MEMBER_VIEW_PAGE_IDS[this.userService.getFirstSegment()!];
    if (pageId) {
      this.isSectionVisible = this.authUtility.isSectionVisible(pageId, 'purchaseRequests', 'PurchaseRequest');
      this.isSectionEditable = this.authUtility.isSectionEditable(pageId, 'purchaseRequests', 'PurchaseRequest');
      this.isSectionDeletable = this.authUtility.isSectionDeletable(pageId, 'purchaseRequests', 'PurchaseRequest');
    } else {
      this.isSectionVisible = true;
      this.isSectionEditable = true;
      this.isSectionDeletable = true;
    }
    this.canApproveReject = pageId ? this.allowListStore.isButtonAllowed(pageId, 'purchaseApproveReject') : true;
    this.purchaseRequestSearchForm = this.fb.group({
      purchaseRequestNumber: [null],
      status: [null],
      vendor: [null]
    });
    this.getPurchaseRequestList();
  }

  /**
   * Fetches Purchase Request List Data
   * @param { type startingPageNumber = number, type currentPage = number}
   * @returns {void}
   */
  getPurchaseRequestList(startingPageNumber: number = 1, currentPage: number = 0, recordsPerPage: number = 20): void {

    const purchaseRequestPayload = this.buildRequestPayload('purchaseRequestList', startingPageNumber, recordsPerPage);
    this.getPurchaseRequestSub = this.listService.getListData(purchaseRequestPayload, 'claims').subscribe(data => {
      const responseData = data?.responsedata;

      if (startingPageNumber > 1) {
        this.purchaseRequestData = [...this.purchaseRequestData, ...responseData];
      } else {
        this.purchaseRequestData = responseData || [];
      }

      // Add display formatted amount
      this.purchaseRequestData.forEach(item => {
        (item as any).totalAmountDisplay = this.formatCurrency(item.totalAmount);
      });

      this.purchaseRequestData.forEach(item => {
        if (item && typeof item.status === 'string' && item.status.length) {
          const lower = item.status.toLowerCase();
          item.status = lower.charAt(0).toUpperCase() + lower.slice(1);
        }
      });

      if (this.purchaseRequestsChildComponent) {
        this.purchaseRequestsChildComponent.recordsPerPage = recordsPerPage;
        this.purchaseRequestsChildComponent.resetPagination(startingPageNumber, currentPage);
      }
    })
  }

  private formatCurrency(amount: any): string {
    if (amount === null || amount === undefined || amount === '') {
      return '-';
    }
    const num = Number(amount);
    if (isNaN(num)) {
      return amount;
    }
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
  }

  /**
   * Method to build request payload
   * @param {type pageName = string, type startingPageNumber = number}
   * @returns {any}
   */
  buildRequestPayload(pageName: string, startingPageNumber: number, recordsPerPage: number = 20): any {
    const payload: any = {
      pageName: pageName,
      programCode: this.programName,
      condition: 'and',
      recordsPerPage,
      startingPageNumber: startingPageNumber,
      searchParam: [
        {
          jsonPath: 'participantId',
          value: this.participantId,
          operator: 'Equal',
        },
        // Applied filters ride along so paging keeps the same filtered set.
        ...this.purchaseRequestSearchParams,
      ],
      orderByColumn: ['tasPurchaserequestDetailId'],
      orderBy: ['desc'],
    };

    return payload;
  }

  /**
   * Handles click event on Action column
   * @param {type event = any}
   * @returns {void}
   */
  onAction(event: any): void {
    if (event.action === 'view') {
      this.viewPurchaseRequestData = event?.data;
      this.openModal(event.action, event?.data?.status?.toLowerCase() === 'submitted');
    }
  }

  /**
   * Opens Add/View Purchase Request Modal
   * @param {type mode = "view" | "add" | "edit" | "actions"}
   * @returns {void}
   */
  openModal(mode: "view" | "add" | "edit" | "actions", isPurchaseRequestSubmitted?: boolean): void {
    this.mode = mode === 'view' && this.isInStaffRole && isPurchaseRequestSubmitted ? 'actions' : mode;
    this.consumerData.participantPplId = this.referralDataService.demographicData?.participantPPLId ?? '';

    const dialogConfig: DialogConfig = {
      title: mode === 'add' ? 'Add Purchase Request' : 'View Purchase Request',
      okText: 'Save',
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false,
    };

    this.addDialogId = this.dialogSvc.openDialog(
      this.mode === 'add' ? this.addPurchaseRequestDialog : this.viewPurchaseRequestDialog,
      dialogConfig,
      () => this.handleCancel({ isCloseTriggeredByDialog: true }),
      () => this.handleCancel({ isCloseTriggeredByDialog: true })
    );
  }

  /**
   * Loads purchase request data on page load
   * @returns {void}
   */
  onPurchaseRequestPageLoad(event: any): void {
    this.getPurchaseRequestList(event.startingPageNumber ?? 1, event.first ?? 0, event.rows ?? 20)
  }

  closeAddPurchaseRequestDialog() {
    if (this.addDialogId) {
      this.dialogSvc.closeDialog(this.addDialogId);
      this.addDialogId = null;
    }
  }

  /**
   * Event emitted after new purchase request created
   * @param { type event = any }
   * @returns {void}
   */
  onPurchaseRequestCreated(): void {
    this.closeAddPurchaseRequestDialog();
    this.getPurchaseRequestList(1, 0, this.purchaseRequestsChildComponent?.recordsPerPage ?? 20);
  }

  /**
   * Method to get purchase request data on filter search
   * @returns {void}
   */
  purchaseRequestFilterSearch(): void {
    const formData = this.purchaseRequestSearchForm.value;
    const searchParam: { jsonPath: string; value: any; operator: string }[] = [];

    if (formData.status) {
      searchParam.push({
        jsonPath: 'status',
        value: formData.status,
        operator: 'Equal',
      });
    }
    if (formData.vendor) {
      searchParam.push({
        jsonPath: 'vendorName',
        value: formData.vendor,
        operator: 'Contains',
      });
    }

    // Persist the filters, then reuse the paging-aware loader so page 2+ stays filtered.
    this.purchaseRequestSearchParams = searchParam;
    this.getPurchaseRequestList(1, 0, this.purchaseRequestsChildComponent?.recordsPerPage ?? 20);
  }

  /**
   * Cancels/Closes existing open modal dialog
   * @returns {void}
   */
  handleCancel(event: any): void {
    if (event?.isCloseTriggeredByDialog && this.addDialogId) {
      this.dialogSvc.closeDialog(this.addDialogId);
      this.addDialogId = '';
    }
  }

  ngOnDestroy(): void {
    this.handleCancel({ isCloseTriggeredByDialog: true });
    this.purchaseRequestSearchForm.reset();
    if (this.getPurchaseRequestSub) {
      this.getPurchaseRequestSub.unsubscribe();
    }
  }
}
